import React, { useState, useMemo, useEffect } from "react";
import {
  useTable,
  useRowSelect,
  useFilters,
  usePagination,
  useExpanded
} from "react-table";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { COLUMNS } from "./Columns";
import UserRoleListModal from "../../../modals/UserRoleListModal";
import LimitedAccessModal from "../../../modals/LimitedAccessModal";
import TableActionBtnGroup from "../tableactionbtngroup/TableActionBtnGroup";
import "./accessmanagement.scss";
import {
  action,
  createAccPage,
  createPricingPlan,
  noDataPresent,
  successToastContent
} from "../../../../locales/en";
import { useSelector } from "react-redux";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import SearchField from "../../../common/searchbox/SearchField";
import DualPagination from "../../../common/DualPagination";
import ListViewFallback from "../../../error-boundary/fallback/ListViewFallback";
import ErrorBoundary from "../../../error-boundary";
import { ROLE_MANAGEMENT_TOOLTIP_CONFIG } from "../../../../utils/constants/tooltipsConfig";
import { EDIT_USER_PERMISSION } from "../../../../route/paths";
import {
  PAGE_INDEX,
  PAGE_SIZE_25,
  showToastType
} from "../../../../utils/constants/appConstants";
import { showToast } from "../../../../utils/utils";
import privateAxios from "../../../../utils/axiosPrivate";
import { getUserManagementPermissionsURL } from "../../../../utils/constants/apiEndPoints";

const PermissionManagementTable = () => {
  const columns = useMemo(() => COLUMNS, []);
  const [permissions, setPermissions] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [expandedRows, setExpandedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState();
  const [selectedRoleId, setSelectedRoleId] = useState();
  const [showLimitedAccess, setShowLimitedAccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const { loading } = useSelector((state) => state.roleManagement);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const handleToggleModal = (rowId) => {
    setSelectedRoleId(rowId);
    setShowModal((prev) => !prev);
  };

  const handleLimitedAccess = (rowId) => {
    setSelectedPermissionId(rowId);
    setShowLimitedAccess((prev) => !prev);
  };
  const loadpermissions = async (pageNumber, pageSize) => {
    try {
      const params = {
        page: pageNumber,
        size: pageSize,
        keyword: searchQuery
      };
      const response = await privateAxios.get(getUserManagementPermissionsURL, {
        params
      });
      const responseData = response.data?.data || [];
      setPermissions(responseData);
      setTotalPages(response.data?.pageInfo?.totalPages || 0);
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType?.error);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data: permissions,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 }
    },
    useFilters,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  useEffect(() => {
    loadpermissions(pageIndex, pageSize);
  }, [pageIndex, pageSize, searchQuery]);

  // Handle changes in the search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      gotoPage(0);
      setSearchQuery(trimmedQuery);
    }
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  return (
    <>
      <div className="d-flex flex-wrap mb-2 justify-content-end search">
        <div className="sm-top-10">
          <SearchField
            tempSearchQuery={tempSearchQuery}
            handleSearchChange={handleSearchChange}
            handleKeyDown={handleKeyDown}
            loading={loading}
          />
        </div>
      </div>
      <ErrorBoundary fallback={<ListViewFallback />}>
        <table
          className="mt-2 mb-3 permission-management-table"
          {...getTableProps()}
        >
          <thead ref={headerRef}>
            {headerGroups.map((headerGroup, i) => (
              <tr
                className="parentrow"
                {...headerGroup.getHeaderGroupProps()}
                key={i}
              >
                <th />
                {headerGroup.headers.map((column) => {
                  return (
                    <th {...column.getHeaderProps()} key={column.id}>
                      <span>{column.render("Header")}</span>
                    </th>
                  );
                })}
                <th className="text-end">{action}</th>
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {loading && (
              <TableSkeleton rows={10} columns={visibleColumnCount} />
            )}
            {!loading && permissions.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} className="text-center">
                  {noDataPresent}
                </td>
              </tr>
            )}
            {!loading &&
              permissions.length > 0 &&
              page.map((row) => {
                prepareRow(row);
                const isExpanded = expandedRows.includes(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <tr className="parentrow" {...row.getRowProps()}>
                      <td className="expand-td">
                        <span
                          className="expandbtn cursor-pointer"
                          onClick={() => toggleRow(row.id)}
                        >
                          {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                        </span>
                      </td>
                      {row.cells.map((cell) => {
                        let cellContent = cell.value;

                        if (cell.column.id === "navItemSlugs") {
                          cellContent =
                            Array.isArray(cell.value) &&
                            cell.value.length > 0 ? (
                              <ul className="permissions_content list-inline mb-0">
                                {cell.value.map((slug, index) => (
                                  <li
                                    className="list-inline-item mb-1"
                                    key={index}
                                  >
                                    <span className="badge border border-dark text-dark rounded-pill cursor-pointer">
                                      {slug}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : null;
                        }

                        if (cell.column.id === "isActive") {
                          cellContent = (
                            <span
                              className={`badge border border-${cell.value ? "success" : "danger"} rounded-pill text-${cell.value ? "success" : "danger"}`}
                            >
                              {cell.value
                                ? createAccPage.active
                                : createPricingPlan.inactive}
                            </span>
                          );
                        }

                        return (
                          <td
                            key={cell.getCellProps().key}
                            {...cell.getCellProps()}
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                      <td>
                        <TableActionBtnGroup
                          editUrl={EDIT_USER_PERMISSION(row.original.id)}
                          tooltips={{
                            ...ROLE_MANAGEMENT_TOOLTIP_CONFIG
                          }}
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="childrow colexpand">
                        <td colSpan={columns.length + 2}>
                          <div className="tdbody">
                            {columns.map((column) => {
                              let content = String(
                                row.original[column.accessor]
                              );

                              if (column.accessor === "navItemSlugs") {
                                const slugs = row.original[column.accessor];

                                content =
                                  Array.isArray(slugs) && slugs.length > 0 ? (
                                    <ul className="permissions_content list-inline mb-0">
                                      {slugs.map((slug, index) => (
                                        <li
                                          className="list-inline-item mb-1"
                                          key={index}
                                        >
                                          <span className="badge border border-dark text-dark rounded-pill cursor-pointer">
                                            {slug}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : null;
                              }

                              if (column.accessor === "isActive") {
                                content = (
                                  <span
                                    className={`badge border border-${content === "true" ? "success" : "danger"} rounded-pill text-${content === "true" ? successToastContent.status : successToastContent.danger}`}
                                  >
                                    {content === "true"
                                      ? createPricingPlan.active
                                      : createPricingPlan.inactive}
                                  </span>
                                );
                              }

                              return (
                                <span
                                  className="mb-2 disabled-clone"
                                  key={column.accessor}
                                >
                                  <strong>{column.Header}:</strong> {content}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </ErrorBoundary>
      {permissions.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={permissions.length}
        />
      )}
      {selectedRoleId && (
        <UserRoleListModal
          isOpen={showModal}
          toggle={handleToggleModal}
          roleId={selectedRoleId}
        />
      )}
      {selectedPermissionId && (
        <LimitedAccessModal
          isOpen={showLimitedAccess}
          toggle={handleLimitedAccess}
          permissionId={String(selectedPermissionId)}
        />
      )}
    </>
  );
};

export default PermissionManagementTable;
